/**
 * Pure H OS · Inventory · Goods Received Notes list.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { inventory, InvGRN } from "../../services/api";

const VENTURE = "orvan";

const STATUS_TINTS: Record<string, { bg: string; fg: string }> = {
  draft:     { bg: "#e0f2fe", fg: "#075985" },
  posted:    { bg: "#dcfce7", fg: "#15803d" },
  cancelled: { bg: "#f1f5f9", fg: "#64748b" },
};

export default function GRNList() {
  const [rows, setRows] = useState<InvGRN[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await inventory.listGRN(VENTURE, status || undefined));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <section data-testid="grn-list">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium" style={{ color: "var(--ph-ink-strong)" }}>Goods Received Notes</h2>
          <div className="text-xs text-slate-500 mt-1">Every physical receipt into a warehouse.</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="grn-status-filter"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="posted">Posted</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <Link
            to="/pureh/inventory/grn/new"
            data-testid="grn-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white"
            style={{ background: "var(--ph-brand-primary)", borderRadius: "var(--ph-radius-sm)" }}
          >
            <Plus size={13} /> New GRN
          </Link>
        </div>
      </div>

      <div className="overflow-hidden" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
        <table className="w-full text-sm" data-testid="grn-table">
          <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
            <tr>
              <th className="text-left px-3 py-2">Doc #</th>
              <th className="text-left px-3 py-2">Supplier</th>
              <th className="text-left px-3 py-2">PO ref</th>
              <th className="text-left px-3 py-2">Lines</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-xs">No GRNs yet.</td></tr>
            )}
            {rows.map((g) => (
              <tr key={g.id} data-testid={`grn-row-${g.id}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                <td className="px-3 py-2 font-mono text-xs">
                  <Link to={`/pureh/inventory/grn/${g.id}`} className="hover:underline" data-testid={`grn-link-${g.id}`}>
                    {g.docNumber}
                  </Link>
                </td>
                <td className="px-3 py-2">{g.supplierName}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{g.supplierRef || "—"}</td>
                <td className="px-3 py-2 text-xs">{g.lineCount} · {g.totalUnits} units</td>
                <td className="px-3 py-2 text-xs">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: STATUS_TINTS[g.status]?.bg, color: STATUS_TINTS[g.status]?.fg }}>
                    {g.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(g.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
